import { useState } from "react";
import { Plus } from "lucide-react";
import { CATALOG, CANDLE_NAMES, fmt } from "@/data/catalog";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

// Candle cross-sell shown on the mask PDP and in the cart (pre-checkout).
export const CandleUpsell = ({ className = "", soft = false, compact = false }) => {
  const [sel, setSel] = useState(CANDLE_NAMES[0]);
  const { addItem } = useCart();
  const c = CATALOG[sel];

  const add = () => {
    addItem(sel, 1);
    toast.success(`${c.name} added to your order`, { description: "The perfect glow-time companion." });
  };

  const container = soft
    ? "border-[#F2E8E6] bg-[#FFFCFA]"
    : "border-[#F6D8C2] bg-gradient-to-br from-[#FFFCFA]/70 to-[#FFFCFA]/50";

  if (compact) {
    return (
      <div data-testid="candle-upsell" className={`overflow-hidden rounded-2xl border ${container} p-3.5 ${className}`}>
        <div className="flex items-center gap-3">
          <img src={c.image} alt={c.name} className="h-12 w-12 flex-none rounded-xl border border-white/60 object-cover" />
          <div className="min-w-0 flex-1">
            <p className="text-[0.56rem] font-semibold uppercase tracking-[0.16em] text-[#FBC4A8]">Complete your ritual</p>
            <p className="font-display text-base leading-tight text-[#2C2B2C]">Add a Café-Luxe Candle</p>
            <p className="text-[0.68rem] text-[#9A8E88]"><span className="text-[#2C2B2C]">{fmt(50)}</span> <span className="line-through">{fmt(65)}</span></p>
          </div>
        </div>
        <div className="mt-3 -mx-0.5 flex gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {CANDLE_NAMES.map((sku) => {
            const active = sel === sku;
            return (
              <button key={sku} onClick={() => setSel(sku)} data-testid={`candle-scent-${sku}`}
                className={`flex-none whitespace-nowrap rounded-full border px-2.5 py-1 text-[0.7rem] transition-all ${active ? "border-[#FBC4A8] bg-[#FBC4A8] font-medium text-[#232222]" : "border-[#F2E8E6] bg-white/70 text-[#8A7F86] hover:border-[#FBC4A8]"}`}>
                {CATALOG[sku].name}
              </button>
            );
          })}
        </div>
        <button onClick={add} data-testid="candle-upsell-add"
          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full bg-[#FBC4A8] py-2.5 text-[0.7rem] font-medium uppercase tracking-[0.14em] text-[#2C2B2C] transition-all hover:bg-[#F6B393]">
          <Plus className="h-3.5 w-3.5" /> Add {c.name}, {fmt(50)}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="candle-upsell" className={`overflow-hidden rounded-[1.6rem] border ${container} p-5 ${className}`}>
      <div className="flex items-center gap-4">
        <img src={c.image} alt={c.name} className="h-16 w-16 flex-none rounded-2xl border border-white/60 object-cover" />
        <div className="min-w-0">
          <p className="text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-[#FBC4A8]">Complete your ritual</p>
          <p className="font-display text-xl leading-tight text-[#2C2B2C]">Add a Café-Luxe Candle</p>
          <p className="mt-0.5 text-xs text-[#8A7F86]">Set the mood for glow time · <span className="text-[#2C2B2C]">{fmt(50)}</span> <span className="line-through">{fmt(65)}</span></p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {CANDLE_NAMES.map((sku) => {
          const active = sel === sku;
          return (
            <button key={sku} onClick={() => setSel(sku)} data-testid={`candle-scent-${sku}`}
              className={`rounded-full border px-3 py-1.5 text-xs transition-all ${active ? "border-[#FBC4A8] bg-[#FBC4A8] text-[#232222] font-medium" : "border-[#F2E8E6] bg-white/70 text-[#8A7F86] hover:border-[#FBC4A8]"}`}>
              {CATALOG[sku].name}
            </button>
          );
        })}
      </div>

      <button onClick={add} data-testid="candle-upsell-add"
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#FBC4A8] py-3 text-xs uppercase tracking-[0.18em] text-[#2C2B2C] transition-all hover:bg-[#F6B393] hover:tracking-[0.22em]">
        <Plus className="h-4 w-4" /> Add {c.name}, {fmt(50)}
      </button>
    </div>
  );
};
